import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiConfig from '../../apiConfig';

type Payslip = {
  staff_name: string;
  store_name: string;
  company_name?: string | null;
  payroll_month: string;
  period_start: string;
  period_end: string;
  payment_date: string | null;
  finalized_at: string | null;
  pay_type: 'hourly' | 'daily' | 'monthly' | string;
  scheduled_days: number;
  scheduled_minutes: number;
  payable_minutes: number;
  shortage_minutes: number;
  paid_leave_days: number;
  absent_days: number;
  paid_early_work_minutes: number;
  paid_overtime_minutes: number;
  late_days: number;
  early_leave_days: number;
  base_pay: number;
  extra_pay: number;
  incentive_pay: number;
  adjustment_pay: number;
  deduction_pay: number;
  salary_tax_applied: boolean;
  salary_tax_amount_usd: number;
  take_home_pay: number;
  net_pay: number;
  hourly_rate: number;
};

const payTypeLabels: Record<string, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  monthly: 'Monthly',
};

const formatMoney = (value: number) => `$${Number(value || 0).toFixed(2)}`;

const roundedMoney = (value: number) => Math.round(Number(value || 0) * 100) / 100;

const calculateGrossPay = (payslip: Payslip) => roundedMoney(
  payslip.base_pay + payslip.extra_pay + payslip.incentive_pay + payslip.adjustment_pay,
);

const calculateNetPay = (payslip: Payslip) => roundedMoney(
  payslip.take_home_pay ?? Math.max(
    0,
    calculateGrossPay(payslip) - payslip.deduction_pay - Number(payslip.salary_tax_amount_usd ?? 0),
  ),
);

const calculateExcessDeduction = (payslip: Payslip) => roundedMoney(Math.max(0, payslip.deduction_pay - calculateGrossPay(payslip)));

const formatMinutes = (value: number) => {
  const total = Math.max(0, Math.round(Number(value || 0)));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};

const StaffPayslipPage = () => {
  const { token } = useParams();
  const [payslip, setPayslip] = useState<Payslip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setError('Payslip link is invalid.');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${apiConfig.baseURL}/api/timecard/public/payroll-payslips/${token}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error ?? 'Failed to load payslip.');
        if (!cancelled) setPayslip(data.payslip as Payslip);
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? 'Failed to load payslip.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-10 text-slate-700">
        <div className="mx-auto max-w-xl rounded-2xl bg-white p-6 shadow-sm">Loading payslip...</div>
      </main>
    );
  }

  if (error || !payslip) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-10 text-slate-700">
        <div className="mx-auto max-w-xl rounded-2xl border border-red-100 bg-white p-6 text-red-700 shadow-sm">
          {error ?? 'Payslip not found.'}
        </div>
      </main>
    );
  }

  const netPay = calculateNetPay(payslip);
  const excessDeduction = calculateExcessDeduction(payslip);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-900">
      <section className="mx-auto max-w-xl overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <p className="text-sm font-medium text-emerald-700">{payslip.company_name || payslip.store_name}</p>
          <h1 className="mt-1 text-2xl font-bold">Payslip</h1>
          <p className="mt-1 text-sm text-slate-500">
            {payslip.payroll_month} / {payslip.period_start} - {payslip.period_end}
          </p>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Staff</div>
              <div className="mt-1 font-semibold">{payslip.staff_name}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Store</div>
              <div className="mt-1 font-semibold">{payslip.store_name}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Company</div>
              <div className="mt-1 font-semibold">{payslip.company_name || '-'}</div>
            </div>
            <div className="rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Pay type</div>
              <div className="mt-1 font-semibold">{payTypeLabels[payslip.pay_type] ?? payslip.pay_type}</div>
            </div>
            <div className="col-span-2 rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Work period</div>
              <div className="mt-1 font-semibold">{payslip.period_start} - {payslip.period_end}</div>
            </div>
            <div className="col-span-2 rounded-xl bg-slate-50 p-4">
              <div className="text-xs font-medium text-slate-500">Payment date</div>
              <div className="mt-1 font-semibold">{payslip.payment_date || '-'}</div>
            </div>
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-5">
            <div className="text-sm font-medium text-emerald-700">Net pay</div>
            <div className="mt-1 text-3xl font-bold text-emerald-950">{formatMoney(netPay)}</div>
            {excessDeduction > 0 && (
              <div className="mt-2 text-xs font-medium text-orange-700">
                Deduction exceeds payable earnings by {formatMoney(excessDeduction)}.
              </div>
            )}
          </div>

          <dl className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {[
              ['Scheduled', `${payslip.scheduled_days} days / ${formatMinutes(payslip.scheduled_minutes)}`],
              ['Payable time', formatMinutes(payslip.payable_minutes)],
              ['Shortage time', formatMinutes(payslip.shortage_minutes)],
              ['Paid leave / Absence', `${payslip.paid_leave_days} / ${payslip.absent_days}`],
              ['Late / Early leave', `${payslip.late_days} / ${payslip.early_leave_days}`],
              ['Paid early work', formatMinutes(payslip.paid_early_work_minutes)],
              ['Paid overtime', formatMinutes(payslip.paid_overtime_minutes)],
              ['Hourly rate', formatMoney(payslip.hourly_rate)],
              ['Base pay', formatMoney(payslip.base_pay)],
              ['Out-of-schedule pay', formatMoney(payslip.extra_pay)],
              ['Incentive', formatMoney(payslip.incentive_pay)],
              ['Adjustment', formatMoney(payslip.adjustment_pay)],
              ['Deduction', formatMoney(payslip.deduction_pay)],
              [
                'Salary tax',
                payslip.salary_tax_applied === false
                  ? 'Not applied'
                  : formatMoney(payslip.salary_tax_amount_usd),
              ],
              ...(excessDeduction > 0 ? [['Excess deduction', formatMoney(excessDeduction)]] : []),
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-sm text-slate-500">{label}</dt>
                <dd className="text-sm font-semibold text-slate-900">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </main>
  );
};

export default StaffPayslipPage;
